/**
 * Authentication Store using Zustand v5
 *
 * This store manages user authentication state including:
 * - Current user information
 * - Authentication token
 * - Login/logout actions
 * - Persistence of token and user to localStorage
 *
 * Uses Zustand v5 TypeScript pattern with persist middleware.
 * Both token and user are persisted for instant session restoration
 * on page refresh, with background validation via /auth/me.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient } from '@/api/client';
import type { User } from '@/types';
import { DEFAULT_LANDING_PAGE } from '@/types';

/**
 * Authentication API response structure
 */
interface AuthResponse {
  user: User;
  token: string;
}

/**
 * Auth API calls to real backend
 */
const authApi = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>('/auth/login', { email, password }, { skipAuth: true });
  },

  getCurrentUser: async (): Promise<User | null> => {
    try {
      const response = await apiClient.get<{ user: User }>('/auth/me');
      return response.user;
    } catch {
      return null;
    }
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    }
  },
};

/**
 * Authentication state interface
 */
interface AuthState {
  /** Current authenticated user, null if not logged in */
  user: User | null;
  /** Authentication token for API calls */
  token: string | null;
  /** Whether auth state is being loaded/initialized */
  isLoading: boolean;
  /** Error message from last failed auth operation */
  error: string | null;
}

/**
 * Authentication actions interface
 */
interface AuthActions {
  /** Authenticate user with email and password */
  login: (email: string, password: string) => Promise<void>;
  /** Clear authentication state and invalidate token */
  logout: () => Promise<void>;
  /** Set user directly (for session refresh) */
  setUser: (user: User | null) => void;
  /** Set loading state */
  setLoading: (isLoading: boolean) => void;
  /** Clear any error state */
  clearError: () => void;
  /** Initialize auth state from stored token */
  initialize: () => Promise<void>;
  /** Get the default landing page for current user's role */
  getDefaultLandingPage: () => string;
}

/**
 * Combined auth store type
 */
type AuthStore = AuthState & AuthActions;

/**
 * Authentication store with Zustand v5 and persist middleware
 *
 * Uses the recommended v5 TypeScript pattern:
 * `create<State>()(middleware((set, get) => ({...})))`
 *
 * Both token and user are persisted to localStorage for instant session
 * restoration on page refresh. User data is validated in background.
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isLoading: true, // Start loading until initialized
      error: null,

      // Actions
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const { user, token } = await authApi.login(email, password);
          set({
            user,
            token,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Login failed';
          set({
            user: null,
            token: null,
            isLoading: false,
            error: message,
          });
          throw error; // Re-throw for UI handling
        }
      },

      logout: async () => {
        set({ isLoading: true });

        try {
          await authApi.logout();
        } finally {
          // Always clear state even if API call fails
          set({
            user: null,
            token: null,
            isLoading: false,
            error: null,
          });
        }
      },

      setUser: (user: User | null) => {
        set({ user });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      clearError: () => {
        set({ error: null });
      },

      initialize: async () => {
        let { token, user } = get();

        // Fallback: read directly from localStorage if store not yet hydrated
        if (!token) {
          try {
            const raw = localStorage.getItem('rwa-token-auth-storage');
            if (raw) {
              const parsed = JSON.parse(raw);
              token = parsed?.state?.token ?? null;
              user = parsed?.state?.user ?? null;
              if (token) {
                set({ token, ...(user ? { user } : {}) });
              }
            }
          } catch {
            // Corrupted storage data, ignore
          }
        }

        if (!token) {
          set({ isLoading: false });
          return;
        }

        // If user is already available (from persist), show app immediately
        if (user) {
          set({ isLoading: false });
        }

        // Validate token with backend in background
        // IMPORTANT: never clear a persisted user on background validation
        // failure — only explicit logout or confirmed 401 should clear state
        try {
          const freshUser = await authApi.getCurrentUser();
          if (freshUser) {
            set({ user: freshUser, isLoading: false });
          } else {
            // Only clear if we had no persisted user to begin with
            if (!user) {
              set({ user: null, token: null, isLoading: false });
            }
          }
        } catch {
          // Keep persisted user on network/transient errors
          set({ isLoading: false });
        }
      },

      getDefaultLandingPage: () => {
        const { user } = get();
        if (!user) {
          return '/login';
        }
        return DEFAULT_LANDING_PAGE[user.role];
      },
    }),
    {
      name: 'rwa-token-auth-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Persist both token and user for instant session restoration
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
      // Handle hydration from storage
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            // Storage rehydration failed
            state?.setLoading(false);
          } else if (state) {
            // Token loaded from storage, initialize user data
            state.initialize();
          }
        };
      },
    }
  )
);

/**
 * Selector hooks for common auth state access patterns
 * These optimize re-renders by selecting only needed state
 */

/** Get current user */
export const useCurrentUser = () => useAuthStore((state) => state.user);

/** Get current user's role */
export const useUserRole = () => useAuthStore((state) => state.user?.role);

/** Check if user is authenticated */
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.user !== null && state.token !== null);

/** Get loading state */
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);

/** Get auth error */
export const useAuthError = () => useAuthStore((state) => state.error);

/** Get auth token for API calls */
export const useAuthToken = () => useAuthStore((state) => state.token);
